import {
  Link as CarbonLink,
  StructuredListBody,
  StructuredListCell,
  StructuredListRow,
  StructuredListWrapper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@carbon/react';
import {
  detailRoute,
  hasPermission,
  OverviewColumn,
  OverviewFrame,
  overviewApi,
  StatTile,
  todayRange,
  useAdmin,
  useFormat,
  useOverviewQuery,
  WidgetCard,
  type FleetStoreDto,
} from '@pos/admin-ui';
import { useTranslation } from '@pos/i18n';
import { Fragment, useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import {
  BusinessFilters,
  BusyHours,
  ChannelMix,
  KpiTiles,
  PaymentMix,
  SalesTrend,
  StoreRanking,
  TopItems,
} from './BusinessWidgets';
import { MembersByTier, MemberTiles, NewMembersTrend } from './MemberWidgets';
import { rangeOf, type Period } from './period';

// Permissions as the existing pages use them: fleet and sync need ho.sync.read; the sales figures the sales reports' permission,
// the payment mix the finance reports' (ADR 0116); deliveries ho.orders.read.
const SYNC = 'ho.sync.read';
const ORDERS = 'ho.orders.read';
const SALES = 'ho.reports.sales.read';
const FINANCE = 'ho.reports.finance.read';
const MEMBERS = 'ho.members.accounts.read';

const FLEET_INTERVAL = 30_000;

function useFleet() {
  const { client } = useAdmin();
  const api = useMemo(() => overviewApi(client), [client]);
  return useOverviewQuery(['fleet'], () => api.fleet('24h'), FLEET_INTERVAL);
}

/** Online, offline, never connected and total, from the one fleet call. */
function FleetTiles() {
  const { t } = useTranslation();
  const format = useFormat();
  const fleet = useFleet();
  if (fleet.isError) {
    return (
      <OverviewColumn span={16}>
        <WidgetCard
          title={t('admin.overview.fleet.title')}
          isError
          error={fleet.error}
          onRetry={() => void fleet.refetch()}
        />
      </OverviewColumn>
    );
  }
  const f = fleet.data;
  const tiles = [
    ['online', f?.online],
    ['offline', f?.offline],
    ['neverConnected', f?.neverConnected],
    ['total', f?.total],
  ] as const;
  return (
    <>
      {tiles.map(([key, value]) => (
        <OverviewColumn key={key} span={4}>
          <StatTile
            label={t(`admin.overview.fleet.${key}`)}
            value={value == null ? '' : format.number(value)}
            isLoading={fleet.isPending}
            href="/sync/stores"
          />
        </OverviewColumn>
      ))}
    </>
  );
}

const hasProblem = (s: FleetStoreDto) =>
  !s.online || s.failedMessages > 0 || s.deadMessages > 0 || s.conflicts24h > 0 || !!s.lastError;

function SyncProblems() {
  const { t } = useTranslation();
  const fleet = useFleet();
  const problems = (fleet.data?.stores ?? []).filter(hasProblem).filter((s) => s.status === 'paired');
  return (
    <WidgetCard
      title={t('admin.overview.problems.title')}
      isLoading={fleet.isPending}
      isError={fleet.isError}
      error={fleet.error}
      onRetry={() => void fleet.refetch()}
      isEmpty={problems.length === 0}
      emptyHeading={t('admin.overview.problems.empty')}
    >
      <StructuredListWrapper isCondensed aria-label={t('admin.overview.problems.title')}>
        <StructuredListBody>
          {problems.map((s) => (
            <StructuredListRow key={s.storeId}>
              <StructuredListCell>
                <CarbonLink
                  as={Link}
                  to={detailRoute('org.store', s.storeId)}
                  aria-label={t('admin.overview.problems.open', { store: s.name })}
                >
                  {s.code} · {s.name}
                </CarbonLink>
                <p>
                  {s.online ? '' : `${t('admin.sync.offline')}. `}
                  {t('admin.overview.problems.detail', {
                    outbox: s.outboxDepth,
                    failed: s.failedMessages,
                    dead: s.deadMessages,
                    conflicts: s.conflicts24h,
                  })}
                </p>
                {s.lastError && <p>{s.lastError}</p>}
              </StructuredListCell>
            </StructuredListRow>
          ))}
        </StructuredListBody>
      </StructuredListWrapper>
    </WidgetCard>
  );
}

function DeliveryFailures() {
  const { t } = useTranslation();
  const format = useFormat();
  const { client } = useAdmin();
  const api = useMemo(() => overviewApi(client), [client]);
  const query = useOverviewQuery(['deliveries'], () => api.deliveriesByProvider(todayRange()), 60_000);
  const failing = (query.data ?? []).filter((d) => d.failed > 0);
  return (
    <WidgetCard
      title={t('admin.overview.deliveries.title')}
      isLoading={query.isPending}
      isError={query.isError}
      error={query.error}
      onRetry={() => void query.refetch()}
      isEmpty={failing.length === 0}
      emptyHeading={t('admin.overview.deliveries.empty')}
    >
      <Table aria-label={t('admin.overview.deliveries.title')}>
        <TableHead>
          <TableRow>
            <TableHeader>{t('admin.overview.deliveries.provider')}</TableHeader>
            <TableHeader>{t('admin.overview.deliveries.total')}</TableHeader>
            <TableHeader>{t('admin.overview.deliveries.failed')}</TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {failing.map((d) => (
            <TableRow key={`${d.method}|${d.providerCode ?? ''}`}>
              <TableCell>
                <CarbonLink as={Link} to="/sales/shipping">
                  {d.providerName ?? t('admin.overview.deliveries.own')}
                </CarbonLink>
              </TableCell>
              <TableCell>{format.number(d.count)}</TableCell>
              <TableCell>{format.number(d.failed)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </WidgetCard>
  );
}

/**
 * The HO landing page (FR-RPT-07): the business first, for a period and a store or all of them (sales against the period
 * before, the trend, where sales come from, what sells, which stores lead, when it is busy, how customers pay), then the
 * fleet's health. Built on the HO reports (ADR 0097); each widget links to its full report.
 */
export function HoOverview() {
  const { t } = useTranslation();
  const { session } = useAdmin();
  const can = (permission: string) => hasPermission(session, permission);
  // This month by default: enough days for the trend and the busy hours to say something.
  const [period, setPeriod] = useState<Period>('month');
  const [storeId, setStoreId] = useState('');
  const range = useMemo(() => rangeOf(period, storeId), [period, storeId]);
  const sales = can(SALES);
  const finance = can(FINANCE);
  const members = can(MEMBERS);

  const widgets: { key: string; visible: boolean; span?: 4 | 8 | 16; bare?: boolean; node: ReactNode }[] = [
    { key: 'kpis', visible: sales, bare: true, node: <KpiTiles range={range} /> },
    { key: 'trend', visible: sales, span: 16, node: <SalesTrend range={range} period={period} /> },
    { key: 'channels', visible: sales, node: <ChannelMix range={range} /> },
    { key: 'payments', visible: finance, node: <PaymentMix range={range} /> },
    { key: 'items', visible: sales, node: <TopItems range={range} /> },
    { key: 'stores', visible: sales && !storeId, node: <StoreRanking range={range} /> },
    { key: 'hours', visible: sales && period !== 'today', span: 16, node: <BusyHours range={range} /> },
    // Members (ADR 0129): how many, how many join, by tier.
    { key: 'members', visible: members, bare: true, node: <MemberTiles range={range} /> },
    { key: 'joins', visible: members, node: <NewMembersTrend range={range} /> },
    { key: 'tiers', visible: members, node: <MembersByTier range={range} /> },
    { key: 'fleet', visible: can(SYNC), bare: true, node: <FleetTiles /> },
    { key: 'problems', visible: can(SYNC), node: <SyncProblems /> },
    { key: 'deliveries', visible: can(ORDERS), node: <DeliveryFailures /> },
  ];
  const shown = widgets.filter((w) => w.visible);

  return (
    <OverviewFrame
      title={t('admin.nav.overview')}
      description={t('admin.overview.hoHint')}
      isEmpty={shown.length === 0}
      filters={(sales || finance || members) && <BusinessFilters period={period} storeId={storeId} onPeriod={setPeriod} onStore={setStoreId} />}
    >
      {shown.map((w) =>
        // The tiles lay out their own columns.
        w.bare ? (
          <Fragment key={w.key}>{w.node}</Fragment>
        ) : (
          <OverviewColumn key={w.key} span={w.span ?? 8}>
            {w.node}
          </OverviewColumn>
        ),
      )}
    </OverviewFrame>
  );
}
